import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { Bundesland } from '@chancenwerk/db/enums/enums.js';
import { DateTime } from 'luxon';

const FerienAPIEndpoint = 'https://ferien-api.de/api/v1/holidays';

interface FerienAPIResponseEntry {
  start: string;
  end: string;
  year: number;
  stateCode: string;
  name: string;
  slug: string;
}

type FerienAPIResponse = FerienAPIResponseEntry[];

export function bundeslandToStateCode(bundesland: string): string {
  switch (bundesland) {
    case 'Baden-Württemberg':
      return 'BW';
    case 'Bayern':
      return 'BY';
    case 'Berlin':
      return 'BE';
    case 'Brandenburg':
      return 'BB';
    case 'Bremen':
      return 'HB';
    case 'Hamburg':
      return 'HH';
    case 'Hessen':
      return 'HE';
    case 'Mecklenburg-Vorpommern':
      return 'MV';
    case 'Niedersachsen':
      return 'NI';
    case 'Nordrhein-Westfalen':
      return 'NW';
    case 'Rheinland-Pfalz':
      return 'RP';
    case 'Saarland':
      return 'SL';
    case 'Sachsen':
      return 'SN';
    case 'Sachsen-Anhalt':
      return 'ST';
    case 'Schleswig-Holstein':
      return 'SH';
    case 'Thüringen':
      return 'TH';
    default:
      return '';
  }
}

@Entity()
export class HolidayFilter extends AdornisFilter {
  public static override _class = 'HolidayFilter';

  @Field(type => DateTime)
  public start: Maybe<DateTime>;

  @Field(type => DateTime)
  public end: Maybe<DateTime>;

  constructor(doc: PartialEntityData<HolidayFilter>) {
    super(doc);
  }

  public override getMongoFilter() {
    const superFilter = super.getMongoFilter();

    if (!this.start || !this.end) {
      return superFilter;
    }

    const options = [
      new DateTimeAspect({
        fieldName: 'start',
        firstValue: this.start.toJSDate(),
        secondValue: this.end.toJSDate(),
      }).getMongoFilter(),
      new DateTimeAspect({
        fieldName: 'end',
        firstValue: this.start.toJSDate(),
        secondValue: this.end.toJSDate(),
      }).getMongoFilter(),
    ].filter(Boolean);
    const filter = options.length ? { $or: options } : {};

    return {
      $and: [superFilter, filter],
    };
  }
}

@Entity()
export class Holiday extends MongoEntity {
  public static override _class = 'Holiday';
  public static override _collectionName = 'holidays';

  public static override resolveID(entity: any): string {
    return entity.slug;
  }

  @Field(type => DateTime)
  public start: Maybe<DateTime>;

  @Field(type => DateTime)
  public end: Maybe<DateTime>;

  @Field(type => Int)
  public year: Maybe<number>;

  @Field(type => String)
  public stateCode: Maybe<string>;

  @Field(type => String)
  public name: Maybe<string>;

  get capatalizedName() {
    const allBundesländer = Object.values(Bundesland) as string[];

    // Replace lowercase bundesland names with the correct capitalization
    let name = this.name;
    allBundesländer.forEach(b => (name = name?.replace(b.toLowerCase(), b)));

    // Capitalize first character
    name = (name?.charAt(0).toUpperCase() ?? '') + name?.slice(1);

    // Replace (beweglicher ferientag) with (Beweglicher Ferientag)
    name = name.replace('beweglicher ferientag', 'Beweglicher Ferientag');

    return name;
  }

  @Field(type => String)
  public slug: Maybe<string>;

  @Query(type => Int)
  public static getHolidayCount() {
    return async () => {
      const col = await getRawCollection<Holiday>(this._collectionName);
      return await col.countDocuments();
    };
  }

  // Keeps track of the last time the holidays were fetched, to prevent fetching too often
  // and throwing to many requests at the API.
  private static _lastFetch = 0;

  @Query(type => Boolean)
  public static importHolidays() {
    return async () => {
      // Make it impossible to fetch more than once per hour
      if (Date.now() - Holiday._lastFetch < 1000 * 60 * 60) {
        return false;
      }

      const resp = await ((await fetch(FerienAPIEndpoint)).json() as Promise<FerienAPIResponse>);
      const entities = resp.map(entry => {
        return new Holiday({
          start: DateTime.fromISO(entry.start),
          end: DateTime.fromISO(entry.end),
          year: entry.year,
          stateCode: entry.stateCode,
          name: entry.name,
          slug: entry.slug,
        });
      });

      const col = await getRawCollection<Holiday>(this._collectionName);
      const existing = await col
        .find({ _id: { $in: entities.map(e => e.slug!) } }, { projection: { _id: 1 } })
        .toArray();

      for (const entity of entities) {
        if (existing.find(e => e._id === entity.slug)) {
          await entity.save();
        } else {
          await entity.create();
        }
      }

      Holiday._lastFetch = Date.now();
      return true;
    };
  }

  @Query(type => [Holiday])
  public static getHolidays(@Arg('filter', type => HolidayFilter) filter?: Maybe<HolidayFilter>) {
    return async (gqlFields: BaseQLSelectionSet<Holiday>) => {
      const res = await loadMongoData(Holiday, filter ? filter.getMongoFilter() : {});
      return res.map(r => constructValue(r)) as Holiday[];
    };
  }
}
